import { FileText } from 'lucide-react'

/**
 * Read-only spreadsheet viewer — the heavyweight Univer-backed editor
 * was removed because it shipped 79+ locale chunks (hyphenation
 * patterns for every language and historical orthography) that
 * dynamically code-split at runtime. Every deploy invalidated the
 * chunk hashes and existing-user tabs that had cached the old hashes
 * crashed with React error #130 on the next interaction.
 *
 * Trade-off: spreadsheets stop being editable in-app. But:
 *   1. Existing data is preserved (we still parse + render `body.rows`
 *      AND `body.univer.sheets.*.cellData` so admins can SEE every
 *      spreadsheet that was ever saved).
 *   2. Document creation already supports doc / whiteboard for new
 *      content, and most teams using spreadsheets through this panel
 *      were doing simple tabular notes — readable here, exportable
 *      to a real spreadsheet tool (Google Sheets, Excel) when actual
 *      editing is needed.
 *   3. The bundle drops by ~3 MB and every Manager / sub-admin tab
 *      survives a deploy cleanly from this point on.
 *
 * If a real editable spreadsheet feature comes back later, prefer a
 * lighter library (`hot-table`, `react-data-grid`, `gridjs`) or — if
 * Univer is genuinely needed — load it lazily via `React.lazy` so the
 * chunks only ship for users actively editing a spreadsheet.
 */
export function SheetEditor({ initialBody }) {
  const rows = extractRows(initialBody)

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
      <div className="px-5 py-4 border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex items-center gap-2">
        <FileText
          size={16}
          className="text-[color:var(--color-ink-muted)]"
        />
        <p className="text-sm text-[color:var(--color-ink-strong)] font-medium m-0">
          Spreadsheet (read-only)
        </p>
        <span className="ml-auto text-xs text-[color:var(--color-ink-muted)]">
          {rows.length} row{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="px-5 py-12 text-center text-sm text-[color:var(--color-ink-muted)] italic m-0">
          This spreadsheet is empty.
        </p>
      ) : (
        <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
          <table className="w-full text-sm">
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={ri}
                  className="border-b border-[color:var(--color-rule)] last:border-b-0"
                >
                  <td className="px-3 py-2 text-xs text-[color:var(--color-ink-subtle)] bg-[color:var(--color-canvas)] border-r border-[color:var(--color-rule)] tabular-nums w-12 text-right">
                    {ri + 1}
                  </td>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-2 align-top text-[color:var(--color-ink)] border-r border-[color:var(--color-rule)] last:border-r-0 whitespace-pre-wrap"
                    >
                      {cell ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** Extracts a 2D string array from either of the two body shapes we've
 *  shipped:
 *    1. Legacy `{ rows: string[][] }` — direct passthrough.
 *    2. Univer snapshot `{ univer: { sheets: { <id>: { cellData } } } }`
 *       — flatten the first sheet's cellData (row -> col -> { v: value })
 *       into a 2D array so existing saves remain visible without us
 *       having to keep Univer loaded.
 *  Anything else returns an empty array. */
function extractRows(body) {
  if (!body || typeof body !== 'object') return []

  if (Array.isArray(body.rows)) {
    return body.rows.map((r) => (Array.isArray(r) ? r.map(stringify) : []))
  }

  const sheets = body?.univer?.sheets
  if (sheets && typeof sheets === 'object') {
    const firstSheetId = body.univer.sheetOrder?.[0] ?? Object.keys(sheets)[0]
    const sheet = sheets[firstSheetId]
    const cellData = sheet?.cellData
    if (!cellData || typeof cellData !== 'object') return []
    let maxRow = -1
    let maxCol = -1
    for (const r of Object.keys(cellData)) {
      const ri = Number(r)
      if (!Number.isFinite(ri)) continue
      maxRow = Math.max(maxRow, ri)
      for (const c of Object.keys(cellData[r] ?? {})) {
        const ci = Number(c)
        if (Number.isFinite(ci)) maxCol = Math.max(maxCol, ci)
      }
    }
    if (maxRow < 0) return []
    const out = []
    for (let r = 0; r <= maxRow; r++) {
      const row = []
      for (let c = 0; c <= maxCol; c++) {
        const cell = cellData[r]?.[c]
        row.push(stringify(cell?.v))
      }
      out.push(row)
    }
    return out
  }

  return []
}

function stringify(v) {
  if (v == null) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return String(v ?? '')
}
